import React from 'react';
import type { EventComponentProps } from 'stream-chat-react';

/** Regex that matches URLs with or without protocol (http/https/www or bare domain). */
const URL_REGEX =
  /(?:https?:\/\/|www\.)[^\s<>"]+|(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+(?:com|org|net|edu|gov|io|co|app|dev|me|info|biz|us|uk|ca|au|de|fr|it|es|nl|ru|jp|cn|in|br|za|tv|cc|ly|to|gg|gle|xyz|tech|ai|so|fm|im)(?:\/[^\s<>"]*)?/g;

/**
 * Splits text into alternating plain-text and URL segments so we can render
 * URLs as clickable <a> tags while leaving the rest as-is.
 */
export function tokenize(text: string): Array<{ type: 'text' | 'url'; value: string }> {
  const tokens: Array<{ type: 'text' | 'url'; value: string }> = [];
  let lastIndex = 0;

  for (const match of text.matchAll(URL_REGEX)) {
    const start = match.index!;
    if (start > lastIndex) {
      tokens.push({ type: 'text', value: text.slice(lastIndex, start) });
    }
    tokens.push({ type: 'url', value: match[0] });
    lastIndex = start + match[0].length;
  }

  if (lastIndex < text.length) {
    tokens.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return tokens;
}

/**
 * Custom Stream Chat MessageSystem component that renders system/admin messages
 * with clickable links for any URLs found in the message text.
 *
 * Pass this as the `MessageSystem` prop on the <Channel> component.
 */
export const LinkedSystemMessage: React.FC<EventComponentProps> = ({ message }) => {
  const text = message.text ?? '';
  const tokens = tokenize(text);

  return (
    <div className="str-chat__message--system">
      <div className="str-chat__message--system__text">
        <div className="str-chat__message--system__line" />
        <p>
          {tokens.map((token, i) =>
            token.type === 'url' ? (
              <a
                key={i}
                href={token.value.match(/^https?:\/\//) ? token.value : `https://${token.value}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {token.value}
              </a>
            ) : (
              <React.Fragment key={i}>{token.value}</React.Fragment>
            )
          )}
        </p>
        <div className="str-chat__message--system__line" />
      </div>
      {message.created_at && (
        <div className="str-chat__message--system__date">
          {new Date(message.created_at as string | number | Date).toLocaleString()}
        </div>
      )}
    </div>
  );
};
